import { useEffect, useState } from "react";
import type { WidgetProps } from "../registry";
import type { MultipleChoicePayload } from "../../../generated-api";

export type MultipleChoiceResult = {
  answers: Array<{
    questionId: string;
    value: string | string[];
  }>;
};

export function MultipleChoiceWidget({
  payload,
  onComplete,
}: WidgetProps<MultipleChoicePayload>) {
  const { questions } = payload;
  const [answers, setAnswers] = useState<Map<string, string | string[]>>(
    new Map(),
  );

  useEffect(() => {
    if (!questions || questions.length === 0) {
      onComplete({ answers: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!questions || questions.length === 0) {
    return null;
  }

  const allAnswered = questions.every((q) => {
    const answer = answers.get(q.id);
    if (q.multiSelect) {
      return Array.isArray(answer) && answer.length > 0;
    }
    return typeof answer === "string";
  });

  const handleSingleSelect = (questionId: string, value: string) => {
    setAnswers((prev) => new Map(prev).set(questionId, value));
  };

  const handleMultiSelect = (
    questionId: string,
    option: string,
    checked: boolean,
  ) => {
    setAnswers((prev) => {
      const current = (prev.get(questionId) as string[] | undefined) ?? [];
      const next = checked
        ? [...current, option]
        : current.filter((v) => v !== option);
      return new Map(prev).set(questionId, next);
    });
  };

  const handleSubmit = () => {
    const result: MultipleChoiceResult = {
      answers: questions.map((q) => ({
        questionId: q.id,
        value: answers.get(q.id) as string | string[],
      })),
    };
    onComplete(result);
  };

  return (
    <div className="flex flex-col gap-4 mt-1">
      {questions.map((q) => (
        <div key={q.id} className="flex flex-col gap-2">
          <p className="text-sm font-medium text-text-primary">{q.text}</p>
          <div className="flex flex-col gap-1.5">
            {q.options.map((rawOption, oi) => {
              const option =
                typeof rawOption === "string"
                  ? rawOption
                  : ((rawOption as { label?: string; text?: string })?.label ??
                    (rawOption as { label?: string; text?: string })?.text ??
                    String(oi));
              if (q.multiSelect) {
                const selected =
                  (answers.get(q.id) as string[] | undefined) ?? [];
                return (
                  <label
                    key={`${q.id}-${oi}`}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selected.includes(option)}
                      onChange={(e) =>
                        handleMultiSelect(q.id, option, e.target.checked)
                      }
                      className="rounded border-border"
                    />
                    <span className="text-sm text-text-primary">{option}</span>
                  </label>
                );
              }
              const selected = answers.get(q.id) as string | undefined;
              return (
                <label
                  key={`${q.id}-${oi}`}
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <input
                    type="radio"
                    name={q.id}
                    value={option}
                    checked={selected === option}
                    onChange={() => handleSingleSelect(q.id, option)}
                    className="border-border"
                  />
                  <span className="text-sm text-text-primary">{option}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
      <button
        type="button"
        onClick={handleSubmit}
        disabled={!allAnswered}
        className="self-start px-4 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Submit
      </button>
    </div>
  );
}
